import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setColorMode } from 'app/slices/ahuBoardSlice';
import { ahuBoardBus } from '../bus/ahuBoardBus';
import { writeCanvasColorMode } from '../ahuBoardStorage';

/**
 * Phase 6b — PaletteView port (view/canvas/palette.view.js §2.5 +
 * tmpl/canvas/color_chart.tmpl §11.2): renders BOTH children of
 * #pallete-wrapper — #chart-wrapper (the chart, `.hide` while closed) and
 * #palette-icon (toggle). Wired now because the canvas color-mode channel is
 * a 6b deliverable: the `.js-toggle-color` checkbox flips showColor, persists
 * localStorage `canvas_color_mode` ([NEG]#9 — string write, comparison read)
 * and emits `change_canvas_color_mode` (canvas: showColor + redraw, parity
 * 32/89).
 *
 * Outside click closes the chart (ignores #palette-icon — palette.view.js
 * handleOutsideClick). The legacy showList template flag is the open state.
 */
const PaletteChart = () => {
  const dispatch = useAppDispatch();
  const showColor = useAppSelector((s) => s.ahuBoard.canvas.colorMode);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // document click-outside closes (palette.view.js handleOutsideClick).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest('#palette-icon')) return;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const toggleColorMode = () => {
    const next = !showColor;
    dispatch(setColorMode(next));
    writeCanvasColorMode(next);
    ahuBoardBus.emit('change_canvas_color_mode', next);
  };

  return (
    <>
      <div
        id="chart-wrapper"
        ref={wrapperRef}
        className={`color-chart-container${open ? '' : ' hide'}`}
      >
        {/* color_chart.tmpl — markup verbatim (§11.2). */}
        <div className="color-chart-header">
          <h4>AHU Model Color Preferences</h4>
          <div className="color-toggle">
            <label>
              <input
                type="checkbox"
                className="js-toggle-color"
                checked={showColor}
                onChange={toggleColorMode}
              />
              Show in color
            </label>
          </div>
        </div>
        <div className="color-chart-body">
          <div className="color-item">Section name is shown in...</div>
          <div className="color-item green">
            <span className="color-box" />
            when its component is selected
          </div>
          <div className="color-item  active-red">
            <span className="color-box" />
            when its component is not selected
          </div>
          <hr />
          <div className="color-item">Accessory is shown in ...</div>
          <div className="color-item active-red">
            <span className="color-box" />
            when it has a related error
          </div>
          <div className="color-item active-orange">
            <span className="color-box" />
            when it has a related warning
          </div>
        </div>
      </div>
      {/* MainView `click #palette-icon` → PaletteView.toggleMode() (§2.4). */}
      <div
        id="palette-icon"
        className="ka-has-tooltip"
        tooltip="Show Color Palette"
        onClick={() => setOpen((o) => !o)}
      >
        <div className=""> </div>
      </div>
    </>
  );
};

export default PaletteChart;
